import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PiggyBank } from "lucide-react";

// Xero's list price less the CASS partner discount, worked out for however many
// organisations somebody pays for. The percentage sells nothing on its own — the
// annual figure is what people act on.
export const parsePrice = (price) => {
  const m = String(price || "").match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : 0;
};

const money = (n) => `£${n.toFixed(2).replace(/\.00$/, "")}`;

export const XeroSavings = ({ cfg, town = "", className = "" }) => {
  const plans = (cfg?.plans || []).filter((p) => p && p.name && parsePrice(p.price));
  const discount = Number(cfg?.cass_discount_pct) || 0;
  const [plan, setPlan] = useState(() => (plans.find((p) => p.name === "Grow") || plans[0] || {}).name || "");
  const [orgs, setOrgs] = useState(1);

  const sums = useMemo(() => {
    const chosen = plans.find((p) => p.name === plan) || plans[0];
    const list = parsePrice(chosen?.price) * orgs;
    const cass = list * (1 - discount / 100);
    return { list, cass, monthly: list - cass, annual: (list - cass) * 12 };
  }, [plan, orgs, plans, discount]);

  if (!plans.length || !discount) return null;

  return (
    <div
      className={`rounded-2xl border border-navy/10 bg-navy text-white p-6 md:p-8 ${className}`}
      data-testid="xero-savings"
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-orange/15 text-orange flex items-center justify-center shrink-0">
          <PiggyBank size={20} />
        </span>
        <div>
          <h3 className="font-display font-bold text-lg">What would you save?</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {town ? `For ${town} businesses paying Xero directly today.` : "Based on Xero's current UK list prices."}
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">Your plan</span>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            data-testid="xero-savings-plan"
            className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-2.5 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-orange/50"
          >
            {plans.map((p) => (
              <option key={p.name} value={p.name} className="text-navy">
                {p.name} — {p.price} a month
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">
            Xero organisations
          </span>
          <input
            type="number"
            min="1"
            max="50"
            value={orgs}
            onChange={(e) => setOrgs(Math.min(50, Math.max(1, Number(e.target.value) || 1)))}
            data-testid="xero-savings-orgs"
            className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-2.5 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-orange/50"
          />
        </label>
      </div>

      <div className="mt-6 pt-6 border-t border-white/10">
        <p className="text-slate-300 text-sm leading-relaxed" data-testid="xero-savings-summary">
          You'd pay{" "}
          <span className="font-display font-bold text-white" data-testid="xero-savings-cass">{money(sums.cass)}</span>{" "}
          instead of{" "}
          <span className="line-through decoration-slate-500" data-testid="xero-savings-list">{money(sums.list)}</span>{" "}
          a month plus VAT
          {orgs > 1 ? ` for ${orgs} organisations` : ""} — a saving of{" "}
          <span className="font-semibold text-white" data-testid="xero-savings-monthly">{money(sums.monthly)}</span> a
          month.
        </p>
        <p className="font-display font-extrabold text-3xl md:text-4xl text-orange mt-3" data-testid="xero-savings-annual">
          {money(sums.annual)} a year
        </p>
        <Link
          to="/contact?service=Xero%20Subscription"
          data-testid="xero-savings-cta"
          className="mt-5 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-6 py-3 rounded-full transition-all hover:-translate-y-0.5"
        >
          Move my subscription to CASS <ArrowRight size={15} />
        </Link>
        <p className="mt-4 text-xs text-slate-500 leading-relaxed">
          Same Xero, same login, same data — only the billing moves. Prices exclude VAT.
        </p>
      </div>
    </div>
  );
};

export default XeroSavings;
